// =============================================================================
// domain/dayView.ts — the Day Overview's intraday curve.
// -----------------------------------------------------------------------------
// Relocated from the old client's `dvIntradayCurve`. THE DATA ONLY: the points,
// the step-and-hold expansion and the day's extremes. The SVG is a component's
// job (D81), and separating them is what makes the extremes testable — they feed
// the prop daily-loss gauge, which is the one number on this tab that can be
// wrong in a way that costs money.
//
// ── 🚨 THE X-AXIS IS CLOCK TIME, WHICH IS THE WHOLE POINT OF THE TAB ──
//
// The dashboard's equity curve is index-spaced: every trade is one step, evenly
// apart. That cannot show *when* in the session the damage happened. Here x is
// minutes-since-midnight, so eight trades in the first twenty minutes look like
// eight trades in the first twenty minutes.
//
// ⚠ x is the EXIT time, falling back to entry: the moment equity actually
// changed. Plotting the entry puts a loss at the point the position was opened,
// which is exactly the reading a trader would use to conclude their entries are
// mistimed.
//
// ── 🚨 TWO CURRENCIES, AND THE SECOND IS NOT DECORATION ──
//
// Everything carries a display value (whatever unit the user picked) AND a
// dollar shadow. The prop daily-loss rail is a FIRM DOLLAR RULE — Apex does not
// care that you are looking at points — so `lowUsd` / `heatLowUsd` stay in
// dollars regardless of the display unit. Collapsing the two would make the
// loss-limit gauge read in points and silently pass a breached account.
//
// ── 🚨 `heatLow` IS NOT `low` ──
//
// `low` is the lowest REALIZED equity. `heatLow` includes each trade's MAE — the
// worst the OPEN position went — which is what a prop daily-loss rule actually
// measures against. A day that never closed a loser but sat $2,000 underwater
// mid-trade has `low = 0` and `heatLow = -2000`, and only one of those is the
// number that ends an account.
// =============================================================================

import { excursionValue, tradeValue, type ContractSpec } from './trade';
import type { PnlUnit } from './types';

/** `"09:31"` or `"09:31:00"` → minutes since midnight. null when unparseable. */
export function hmToMins(s: string | null | undefined): number | null {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(s ?? ''));
  if (!m) return null;
  const h = +(m[1] as string), mi = +(m[2] as string);
  if (h > 23 || mi > 59) return null;
  return h * 60 + mi;
}

export function minsToHm(mins: number): string {
  const h = Math.floor(mins / 60), m = Math.round(mins % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** What the curve reads from a trade. Narrower than a full Trade (D78). */
export interface DayTrade {
  contract: string | null;
  side: string | null;
  qty: number | null;
  timeIn: string | null;
  timeOut: string | null;
  points: number | null;
  netPnl: number | null;
  rrRealized: number | null;
  maePoints: number | null;
  mfePoints: number | null;
  /**
   * ⚠ Required, not optional, and NOT because this file divides by it.
   * `excursionValue` denominates in R when the display unit is R, and a trade
   * with no stop has no R — so the field has to travel or the excursion silently
   * reads as points on an R axis. The one input whose absence is invisible.
   */
  riskPoints: number | null;
}

export interface CurvePoint {
  mins: number;
  contract: string;
  side: string;
  /** The trade's result in the DISPLAY unit. */
  pnl: number;
  /** Running equity in the display unit, after this trade. */
  equity: number;
  /** Running equity BEFORE this trade — the anchor for peak/trough. */
  eBefore: number;
  /** `eBefore + MFE`, or null with no excursion. The best it reached. */
  peak: number | null;
  /** `eBefore − MAE`, or null. The worst heat taken. */
  trough: number | null;
}

export interface IntradayCurve {
  points: CurvePoint[];
  /** Highest realized equity level reached, never below 0. */
  maxGain: number;
  /** Largest peak-to-trough fall in realized equity. */
  maxDD: number;
  /** Lowest realized equity level, never above 0. */
  low: number;
  /** Lowest level INCLUDING open-position heat. See the header. */
  heatLow: number;
  /** `low`, always in dollars — for the prop rail. */
  lowUsd: number;
  /** `heatLow`, always in dollars — for the prop rail. */
  heatLowUsd: number;
  /** True when any trade carried excursion, so the envelope is drawable. */
  hasEnvelope: boolean;
}

const EMPTY: IntradayCurve = {
  points: [], maxGain: 0, maxDD: 0, low: 0, heatLow: 0,
  lowUsd: 0, heatLowUsd: 0, hasEnvelope: false,
};

/**
 * The day's intraday curve.
 *
 * ⚠ The caller has ALREADY narrowed to the day and applied the metrics gate.
 * This does not filter on account or date — passing it a week produces a week's
 * curve on a single-day axis, which is the caller's error to make loudly rather
 * than this function's to guess at.
 */
export function intradayCurve(
  trades: readonly DayTrade[],
  catalogue: readonly ContractSpec[],
  unit: PnlUnit,
): IntradayCurve {
  const raw = trades
    .map((t) => {
      // ⚠ $/point × CONTRACTS. Excursion is a per-contract points magnitude
      // while `netPnl` is already quantity-adjusted, so the dollar shadow of an
      // excursion has to be scaled here or a 5-lot's heat reads as a 1-lot's.
      const dpp = dollarsPerPointOf(t, catalogue) * (Number(t.qty) || 1);
      const mae = t.maePoints;
      return {
        // ⚠ EXIT first. See the header.
        mins: hmToMins(t.timeOut) ?? hmToMins(t.timeIn),
        pnl: tradeValue(t, unit, catalogue) ?? 0,
        pnlUsd: t.netPnl ?? 0,
        contract: t.contract ?? '',
        side: t.side ?? '',
        mfe: excursionValue(t, unit, t.mfePoints, catalogue),
        mae: excursionValue(t, unit, mae, catalogue),
        maeUsd: mae != null && dpp ? Math.abs(mae) * dpp : null,
      };
    })
    .filter((p): p is typeof p & { mins: number } => p.mins != null)
    .sort((a, b) => a.mins - b.mins);

  if (!raw.length) return EMPTY;

  let cum = 0;
  let cumUsd = 0;
  const points: CurvePoint[] = [];
  const troughsUsd: number[] = [];

  for (const p of raw) {
    const eBefore = cum;
    cum += p.pnl;
    const eBeforeUsd = cumUsd;
    cumUsd += p.pnlUsd;
    points.push({
      mins: p.mins, contract: p.contract, side: p.side,
      pnl: p.pnl, equity: cum, eBefore,
      peak: p.mfe != null ? eBefore + p.mfe : null,
      trough: p.mae != null ? eBefore - p.mae : null,
    });
    troughsUsd.push(p.maeUsd != null ? eBeforeUsd - p.maeUsd : Number.POSITIVE_INFINITY);
  }

  // ⚠ Seeded with the 0 START. A day that only ever lost has no positive level,
  // and without the seed its `maxDD` would be measured from the first loss
  // rather than from flat — understating the drawdown by that trade.
  let peak = 0, maxGain = 0, maxDD = 0, low = 0;
  for (const p of points) {
    const running = p.equity;
    if (running > maxGain) maxGain = running;
    if (running < low) low = running;
    if (running > peak) peak = running;
    const dd = peak - running;
    if (dd > maxDD) maxDD = dd;
  }

  // ⚠ The dollar low is walked SEPARATELY, not derived from `low`. The display
  // unit may not be dollars, and on a mixed-instrument day the two curves do not
  // even have the same shape — points across ES and MES are not comparable while
  // their dollars are.
  let lowUsd = 0;
  let runningUsd = 0;
  for (const p of raw) {
    runningUsd += p.pnlUsd;
    if (runningUsd < lowUsd) lowUsd = runningUsd;
  }

  const heatLow = points.reduce(
    (m, p) => (p.trough != null && p.trough < m ? p.trough : m), low,
  );
  const heatLowUsd = troughsUsd.reduce(
    (m, v) => (Number.isFinite(v) && v < m ? v : m), lowUsd,
  );

  return {
    points, maxGain, maxDD, low, heatLow, lowUsd, heatLowUsd,
    hasEnvelope: points.some((p) => p.peak != null || p.trough != null),
  };
}

/** Local helper so the map above stays readable. */
function dollarsPerPointOf(t: DayTrade, catalogue: readonly ContractSpec[]): number {
  if (!t.contract) return 0;
  const upper = t.contract.toUpperCase();
  let best: ContractSpec | null = null;
  for (const c of catalogue) {
    if (!c?.symbol || !upper.startsWith(c.symbol)) continue;
    if (!best || c.symbol.length > best.symbol.length) best = c;
  }
  return best?.dollarsPerPoint ?? 0;
}

export interface StepPoint { mins: number; equity: number }

/**
 * The step-and-hold path: flat at the prior level up to each trade, a vertical
 * jump at the trade, then flat at the final level to the right edge.
 *
 * ⚠ HOLDS FLAT AFTER THE LAST TRADE, and that is deliberate. Equity does not
 * return to zero when you stop trading — a day with one trade should read as a
 * level that persists across the session, not as a sliver at one x position.
 *
 * ⚠ The domain padding is 12% of the traded span, floored at 15 minutes, and
 * clamped to the day. Without a floor a day whose trades span four minutes gets
 * a half-minute of padding and the curve sits jammed against the axis.
 */
export function stepAndHold(
  points: readonly CurvePoint[],
): { steps: StepPoint[]; tMin: number; tMax: number } {
  if (!points.length) return { steps: [], tMin: 0, tMax: 1 };

  const t0 = (points[0] as CurvePoint).mins;
  const tLast = (points[points.length - 1] as CurvePoint).mins;
  const pad = Math.max(15, Math.round((tLast - t0) * 0.12));
  const tMin = Math.max(0, t0 - pad);
  const tMax = Math.min(24 * 60 - 1, tLast + pad);

  const steps: StepPoint[] = [{ mins: tMin, equity: 0 }];
  let prev = 0;
  for (const p of points) {
    steps.push({ mins: p.mins, equity: prev });
    steps.push({ mins: p.mins, equity: p.equity });
    prev = p.equity;
  }
  steps.push({ mins: tMax, equity: prev });

  return { steps, tMin, tMax };
}
